import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Minus, Trash2, ShoppingBag, ArrowLeft } from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { useLanguage } from '../contexts/LanguageContext'
import type { CartItem } from '../contexts/CartContext'

const DELETE_WIDTH = 56
const SWIPE_THRESHOLD = 28
const HINT_STORAGE_KEY = 'cart_swipe_hint_shown'
const HINT_CYCLES = 2
const HINT_SWIPE_DURATION_MS = 380
const HINT_PAUSE_BETWEEN_MS = 180
const HINT_START_DELAY_MS = 500

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

export default function Cart() {
  const navigate = useNavigate()
  const { items, removeItem, updateQuantity, getSubtotal, clearCart } = useCart()
  const { formatPrice, taxRate, serviceChargeRate } = useRestaurant()
  const { t, isRTL } = useLanguage()

  const [revealedIndex, setRevealedIndex] = useState<number | null>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const [hintPlaying, setHintPlaying] = useState(false)
  const [hintOffset, setHintOffset] = useState(0)
  const [confirmSheetOpen, setConfirmSheetOpen] = useState(false)
  const [confirmSheetClosing, setConfirmSheetClosing] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const [confirmPhone, setConfirmPhone] = useState('')
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [orderNotes, setOrderNotes] = useState('')
  const dragRef = useRef({ startX: 0, initialOffset: 0 })
  const hintRafRef = useRef<number | null>(null)
  const hintTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const subtotal = getSubtotal()
  const vatAmount = (subtotal * taxRate) / 100
  const service = (subtotal * serviceChargeRate) / 100
  const total = subtotal + vatAmount + service

  const getRowOffset = (index: number) => {
    if (hintPlaying && index === 0) return hintOffset
    if (draggingIndex === index) return dragOffset
    if (revealedIndex === index) return DELETE_WIDTH
    return 0
  }

  useEffect(() => {
    if (items.length === 0) return
    try {
      if (sessionStorage.getItem(HINT_STORAGE_KEY) === '1') return
    } catch {
      return
    }

    let cancelled = false

    const animate = (
      from: number,
      to: number,
      durationMs: number,
      onComplete: () => void
    ) => {
      const start = performance.now()
      const run = (now: number) => {
        if (cancelled) return
        const elapsed = now - start
        const t = Math.min(1, elapsed / durationMs)
        const eased = easeInOutCubic(t)
        setHintOffset(from + (to - from) * eased)
        if (t < 1) {
          hintRafRef.current = requestAnimationFrame(run)
        } else {
          onComplete()
        }
      }
      hintRafRef.current = requestAnimationFrame(run)
    }

    const startDelay = setTimeout(() => {
      if (cancelled) return
      setHintPlaying(true)
      setHintOffset(0)

      let cycle = 0
      const runNext = () => {
        if (cancelled) return
        if (cycle >= HINT_CYCLES) {
          setHintOffset(0)
          setHintPlaying(false)
          try {
            sessionStorage.setItem(HINT_STORAGE_KEY, '1')
          } catch {
            /* ignore */
          }
          return
        }
        cycle++
        animate(0, DELETE_WIDTH, HINT_SWIPE_DURATION_MS, () => {
          if (cancelled) return
          hintTimeoutsRef.current.push(
            setTimeout(() => {
              if (cancelled) return
              animate(DELETE_WIDTH, 0, HINT_SWIPE_DURATION_MS, () => {
                if (cancelled) return
                hintTimeoutsRef.current.push(
                  setTimeout(runNext, HINT_PAUSE_BETWEEN_MS)
                )
              })
            }, HINT_PAUSE_BETWEEN_MS)
          )
        })
      }

      hintTimeoutsRef.current.push(setTimeout(runNext, 80))
    }, HINT_START_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(startDelay)
      if (hintRafRef.current != null) {
        cancelAnimationFrame(hintRafRef.current)
        hintRafRef.current = null
      }
      hintTimeoutsRef.current.forEach(clearTimeout)
      hintTimeoutsRef.current = []
    }
  }, [items.length])

  const handleSwipeStart = (index: number, clientX: number) => {
    if (hintPlaying && index === 0) {
      setHintPlaying(false)
      if (hintRafRef.current != null) {
        cancelAnimationFrame(hintRafRef.current)
        hintRafRef.current = null
      }
      hintTimeoutsRef.current.forEach(clearTimeout)
      hintTimeoutsRef.current = []
      try {
        sessionStorage.setItem(HINT_STORAGE_KEY, '1')
      } catch {
        /* ignore */
      }
    }
    setDraggingIndex(index)
    const initial = revealedIndex === index ? DELETE_WIDTH : 0
    dragRef.current = { startX: clientX, initialOffset: initial }
    setDragOffset(initial)
  }

  const handleSwipeMove = (index: number, clientX: number) => {
    if (draggingIndex !== index) return
    const { startX, initialOffset } = dragRef.current
    const delta = isRTL ? clientX - startX : startX - clientX
    const next = clamp(0, initialOffset + delta, DELETE_WIDTH)
    setDragOffset(next)
  }

  const handleSwipeEnd = (index: number) => {
    if (draggingIndex !== index) return
    if (dragOffset >= SWIPE_THRESHOLD) setRevealedIndex(index)
    else setRevealedIndex(null)
    setDraggingIndex(null)
    setDragOffset(0)
  }

  const handleDelete = (item: CartItem) => {
    removeItem(item.menu_item_id, item.variant_name, item.addons)
    setRevealedIndex(null)
  }

  const openConfirmSheet = () => {
    setConfirmError(null)
    setConfirmSheetOpen(true)
  }

  const closeConfirmSheet = () => {
    setConfirmSheetClosing(true)
    setTimeout(() => {
      setConfirmSheetOpen(false)
      setConfirmSheetClosing(false)
      setConfirmError(null)
    }, 250)
  }

  const handleConfirmSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setConfirmError(null)
    const digitsOnly = confirmPhone.replace(/\D/g, '')
    if (digitsOnly.length === 0) {
      setConfirmError(t('mobileRequired'))
      return
    }
    if (digitsOnly.length !== 10) {
      setConfirmError(t('phone10Digits'))
      return
    }
    closeConfirmSheet()
    const payload = {
      items: [...items],
      orderNotes: orderNotes.trim() || undefined,
      customerName: confirmName.trim() || undefined,
      customerMobile: digitsOnly
    }
    clearCart()
    navigate('/order-qr', { state: payload })
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <header className="sticky top-0 z-30 bg-[#fafafa] border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
            </button>
            <h1 className="text-base font-bold text-gray-800">{t('yourCart')}</h1>
            <div className="w-9" aria-hidden />
          </div>
        </header>
        <div className="flex flex-col items-center justify-center px-4 py-12 min-h-[50vh]">
          <ShoppingBag className="w-16 h-16 text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('cartEmpty')}</h2>
          <p className="text-gray-600 mb-6 text-center">{t('addItems')}</p>
          <Link
            to="/"
            className="inline-block bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
          >
            {t('browseMenu')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fafafa] pb-28">
      {/* Header: same as Item Detail - back, title, placeholder for symmetry */}
      <header className="sticky top-0 z-30 bg-[#fafafa] border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
          </button>
          <h1 className="text-base font-bold text-gray-800">{t('yourCart')}</h1>
          <div className="w-9" aria-hidden />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4">

        {/* Cart Items - swipe to reveal delete */}
        <div className="space-y-4 mb-6">
          {items.map((item, index) => {
            const unitPrice = item.base_price + (item.variant_price_modifier || 0) + (item.addons_price || 0)
            const itemTotal = unitPrice * item.quantity
            const offset = getRowOffset(index)

            return (
              <div
                key={index}
                className="relative overflow-hidden rounded-xl border border-gray-200"
                style={{ touchAction: 'pan-y' }}
              >
                {/* Delete zone (revealed when swiped) - orange gradient, our colors */}
                <div
                  className={`absolute top-0 bottom-0 w-14 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 ${isRTL ? 'left-0 rounded-l-xl' : 'right-0 rounded-r-xl'}`}
                >
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="p-3 text-white hover:bg-white/20 rounded-lg transition-colors"
                    aria-label="Remove item"
                  >
                    <Trash2 className="w-6 h-6" />
                  </button>
                </div>

                {/* Sliding card - only round the side away from delete so no white curve over orange */}
                <div
                  className={`bg-white border border-gray-200 p-4 transition-transform duration-75 ease-out ${isRTL ? 'rounded-r-xl' : 'rounded-l-xl'}`}
                  style={{
                    transform: isRTL ? `translateX(${offset}px)` : `translateX(-${offset}px)`,
                  }}
                  onTouchStart={(e) => handleSwipeStart(index, e.touches[0].clientX)}
                  onTouchMove={(e) => handleSwipeMove(index, e.touches[0].clientX)}
                  onTouchEnd={() => handleSwipeEnd(index)}
                  onTouchCancel={() => handleSwipeEnd(index)}
                  onMouseDown={(e) => handleSwipeStart(index, e.clientX)}
                  onMouseMove={(e) => draggingIndex === index && e.buttons === 1 && handleSwipeMove(index, e.clientX)}
                  onMouseUp={() => draggingIndex === index && handleSwipeEnd(index)}
                  onMouseLeave={() => draggingIndex === index && handleSwipeEnd(index)}
                >
                  <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {/* Circular image */}
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 shrink-0">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🍽</div>
                      )}
                    </div>

                    {/* Center: code (yellow badge), name, price (+ variant/addons) */}
                    <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {item.code && (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-200 text-amber-900 mb-0.5">
                          {item.code}
                        </span>
                      )}
                      <h3 className="font-bold text-gray-900">{item.name}</h3>
                      {item.variant_name && (
                        <p className="text-sm text-gray-600">{t('size')}: {item.variant_name}</p>
                      )}
                      {item.addons && item.addons.length > 0 && (
                        <p className="text-sm text-gray-600 truncate">{item.addons.join(', ')}</p>
                      )}
                      <p className="font-bold text-orange-500 text-lg mt-0.5">{formatPrice(itemTotal)}</p>
                    </div>

                    {/* Vertical quantity controls on the right */}
                    <div className="flex flex-col items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1, item.variant_name, item.addons)}
                        className="w-8 h-8 rounded-full border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="font-semibold text-gray-900 text-sm w-6 text-center">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1, item.variant_name, item.addons)}
                        className="w-8 h-8 rounded-full border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Notes - just above order summary */}
        <div className="mb-4">
          <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('specialRequests')} ({t('optional')})
          </label>
          <textarea
            value={orderNotes}
            onChange={(e) => setOrderNotes(e.target.value)}
            placeholder={t('specialRequestsPlaceholder')}
            rows={2}
            className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 outline-none resize-none"
          />
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className={`text-xl font-bold text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t('orderSummary')}</h2>
          <div className="space-y-2 mb-4">
            <div className={`flex justify-between text-gray-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{t('subtotal')}</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className={`flex justify-between text-gray-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{t('vat')} ({taxRate}%)</span>
              <span>{formatPrice(vatAmount)}</span>
            </div>
            {service > 0 && (
              <div className={`flex justify-between text-gray-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{t('serviceCharge')}</span>
                <span>{formatPrice(service)}</span>
              </div>
            )}
          </div>
          <div className="border-t pt-4">
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="text-xl font-bold text-gray-900">{t('total')}</span>
              <span className="text-2xl font-bold text-amber-500">{formatPrice(total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom bar: total + proceed to checkout (same style as Item Detail add-to-order bar) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-gray-100 border-t border-gray-200 safe-area-pb">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`}>
            <span className="text-xs text-gray-500">{t('total')}</span>
            <span className="text-lg font-bold text-amber-600">{formatPrice(total)}</span>
          </div>
          <button
            type="button"
            onClick={openConfirmSheet}
            className="flex-1 py-3.5 rounded-xl bg-orange-500 text-white font-bold text-sm uppercase hover:bg-orange-600 transition-colors flex items-center justify-center"
          >
            {t('confirmOrder')}
          </button>
        </div>
      </div>

      {/* Confirm order bottom sheet: full name (optional), phone (required) */}
      {(confirmSheetOpen || confirmSheetClosing) && (
        <>
          <div
            className={`fixed inset-0 z-50 bg-black/40 ${confirmSheetClosing ? 'animate-sheet-overlay-out' : 'animate-sheet-overlay'}`}
            onClick={closeConfirmSheet}
            aria-hidden
          />
          <div
            className={`fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-white shadow-xl pb-safe ${confirmSheetClosing ? 'animate-sheet-slide-down' : 'animate-sheet-slide-up'}`}
            role="dialog"
            aria-label={t('confirmOrder')}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="h-1 w-12 rounded-full bg-gray-300" />
            </div>
            <form onSubmit={handleConfirmSubmit} className="p-4 pt-0 pb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('confirmOrder')}</h3>
              <label className="block mb-3">
                <span className="block text-sm font-medium text-gray-700 mb-1">{t('fullNameOptional')}</span>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={t('name')}
                  className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 outline-none"
                />
              </label>
              <label className="block mb-4">
                <span className="block text-sm font-medium text-gray-700 mb-1">{t('phoneNumberRequired')} <span className="text-red-500">*</span></span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  pattern="[0-9]*"
                  value={confirmPhone}
                  onChange={(e) => setConfirmPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="05xxxxx"
                  maxLength={10}
                  required
                  className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 outline-none"
                />
              </label>
              {confirmError && (
                <p className="text-red-600 text-sm mb-3">{confirmError}</p>
              )}
              <button
                type="submit"
                className="w-full py-3.5 rounded-xl bg-orange-500 text-white font-bold text-sm uppercase hover:bg-orange-600 transition-colors"
              >
                {t('proceedToCheckout')}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  )
}

